// Collects the browser-side data set HMRC's fraud-prevention headers guide requires on
// every ITSA-bound request (SI 2019/360; WEB_APP_VIA_SERVER connection method). This
// module only COLLECTS the values — attaching them as Gov-Client-* headers at the
// apiCall seam is a separate ticket (RF2).
//
// Documented omission (RF3): Local-IPs and MAC-Addresses are not collected here. The
// Intuit `user-data-for-fraud-prevention` package (github.com/intuit/user-data-for-fraud-prevention,
// ISC licensed — permissive, would have been adoptable on licensing grounds alone) was
// evaluated as a source for WebRTC-derived Local-IPs, but adoption was rejected: (1) RF1's
// collection list does not include Local-IPs — it remains a documented omission under RF3,
// to be resolved via the HMRC missing-data correspondence (RF4), not a frontend library
// choice; (2) verifying whether the package's WebRTC probe yields real (non-empty, non-mDNS)
// values on current Edge/Chrome requires a live browser session, which this environment
// cannot execute — that verification is escalated to a human for if/when Local-IPs is
// revisited. Hand-rolling the six required fields below avoids taking on an unverified
// dependency for data this ticket isn't required to produce.

const DEVICE_ID_LOCALSTORAGE_KEY = 'ospreyFraudPreventionDeviceId'

const generateUuidFallback = (): string => {
  // Only used when crypto.randomUUID is unavailable (e.g. non-secure context). Not
  // cryptographically strong, but a device identifier here needs uniqueness, not secrecy.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

const mintUuid = (): string => (globalThis.crypto as any)?.randomUUID?.() ?? generateUuidFallback()

// Minted once per browser/device and persisted, per HMRC's Device-ID requirement.
export const getDeviceId = (): string => {
  const existing = localStorage.getItem(DEVICE_ID_LOCALSTORAGE_KEY)
  if (existing) return existing

  const deviceId = mintUuid()
  localStorage.setItem(DEVICE_ID_LOCALSTORAGE_KEY, deviceId)
  return deviceId
}

const padTwoDigits = (value: number): string => String(value).padStart(2, '0')

// HMRC wants UTC±HH:MM. Date.getTimezoneOffset() returns minutes BEHIND UTC (positive
// west of UTC), which is the inverse sign of what's required, so it's negated here.
export const getTimezone = (): string => {
  const offsetMinutes = -new Date().getTimezoneOffset()
  const sign = offsetMinutes >= 0 ? '+' : '-'
  const absMinutes = Math.abs(offsetMinutes)
  const hours = Math.floor(absMinutes / 60)
  const minutes = absMinutes % 60
  // The literal "UTC" prefix is part of HMRC's required shape (validator: "submitted as
  // UTC±<hh>:<mm>") — "+01:00" alone is rejected as INVALID_HEADER.
  return `UTC${sign}${padTwoDigits(hours)}:${padTwoDigits(minutes)}`
}

export const getScreens = (): string => {
  const { width, height, colorDepth } = window.screen
  const scalingFactor = window.devicePixelRatio
  return `width=${width}&height=${height}&scaling-factor=${scalingFactor}&colour-depth=${colorDepth}`
}

export const getWindowSize = (): string => `width=${window.innerWidth}&height=${window.innerHeight}`

export const getJsUserAgent = (): string => navigator.userAgent

// navigator.doNotTrack varies by browser: '1'/'yes' (enabled), '0'/'no' (disabled), or
// 'unspecified'/null/undefined (no preference set). Normalised to a boolean per HMRC's
// true/false contract; no preference is treated as false (not tracked-as-disabled).
export const getDoNotTrack = (): boolean => {
  const value = navigator.doNotTrack
  return value === '1' || value === 'yes' || value === true
}

export type FraudPreventionData = {
  deviceId: string
  timezone: string
  screens: string
  windowSize: string
  jsUserAgent: string
  doNotTrack: boolean
}

// Collects the full set immediately before an ITSA-bound request. deviceId is stable
// across calls/sessions (persisted); every other value is regenerated fresh per call.
export const collectFraudPreventionData = (): FraudPreventionData => ({
  deviceId: getDeviceId(),
  timezone: getTimezone(),
  screens: getScreens(),
  windowSize: getWindowSize(),
  jsUserAgent: getJsUserAgent(),
  doNotTrack: getDoNotTrack()
})
